import { PubSubService } from '../core/pubsub-service';
import type { ServiceContext, ServiceRegistration } from '../core/types';
import type { ViewMode } from '../display';
import { ViewportService } from './viewport-service';
import type { AnnotationData } from '../domain/snapshot-annotation';
import { VIEWPORT_TYPE_TO_VIEW_MODE } from '../viewports/types';

// ============================================================================
// Types
// ============================================================================

/** Which native capture variant the user picked as annotation/display base. */
export type SnapshotBaseVariant = 'overlay' | 'no-overlay';

/** Whether a snapshot was taken by the user or generated from a report template. */
export type SnapshotOrigin = 'user' | 'template';

export interface ViewportSnapshot {
    /** Unique identifier for the snapshot. */
    id: string;
    /** Captured ViewMode. */
    viewMode: ViewMode;
    /** ID of the source viewport. */
    viewportId: string;
    /** Native capture WITH viewport overlays (base64 data URL). Immutable after capture. */
    dataUrl: string;
    /** Native capture WITHOUT overlays. Immutable. Absent for template captures. */
    dataUrlNoOverlay?: string;
    /** Which native variant the user chose as the annotation/display base. */
    baseVariant: SnapshotBaseVariant;
    /** Serialized annotations drawn on the chosen base (image-natural-pixel coords). */
    annotations: AnnotationData[];
    /**
     * Cached composite of base image + annotations. Recomputed on every
     * annotation save; absent when there are no annotations. Display-only cache —
     * the native captures above are never mutated.
     */
    annotatedDataUrl?: string;
    /** ISO 8601 timestamp of the capture. */
    capturedAt: string;
    /** Optional label from the capture payload. */
    label?: string;
    /** User-defined description (max 500 chars). */
    description?: string;
    /** Whether this snapshot should be included in the report. Defaults to true. */
    includedInReport: boolean;
    /**
     * Whether the snapshot was taken by the user (`captureDraft`/`saveDraft`)
     * or generated from a report template (`captureViewportById`). Required so
     * the persistence sync hook can POST only user snapshots without inferring
     * origin from unrelated fields (e.g. the presence of `dataUrlNoOverlay`).
     */
    origin: SnapshotOrigin;
    /** For template-origin snapshots: the report template id (`template.id`). */
    templateConfigId?: string;
    /** For template-origin snapshots: the per-config id within the template (`config.id`). */
    configId?: string;
}

/** Native image the snapshot's annotations are drawn on. */
export function getSnapshotBaseUrl(snapshot: ViewportSnapshot): string {
    return snapshot.baseVariant === 'no-overlay'
        ? (snapshot.dataUrlNoOverlay ?? snapshot.dataUrl)
        : snapshot.dataUrl;
}

/** Image to display: the baked composite when annotated, otherwise the native base. */
export function getSnapshotDisplayUrl(snapshot: ViewportSnapshot): string {
    return snapshot.annotatedDataUrl ?? getSnapshotBaseUrl(snapshot);
}

/** Result of a template capture pass, kept so it can be restored on remount. */
export interface TemplateCaptureResult {
    /** Stored snapshot IDs, in config order. */
    ids: string[];
    /** Data signature these snapshots were captured from (null = no signal). */
    signature: string | null;
}

// ============================================================================
// Service
// ============================================================================

export class ViewportSnapshotService extends PubSubService {
    static readonly SERVICE_NAME = 'ViewportSnapshotService';

    static readonly REGISTRATION: ServiceRegistration = {
        name: ViewportSnapshotService.SERVICE_NAME,
        factory: (context: ServiceContext) => new ViewportSnapshotService(context),
    };

    readonly EVENTS = {
        /** Snapshot persisted. Payload: ViewportSnapshot */
        SNAPSHOT_STORED: 'snapshot:stored',
        /** Snapshot removed. Payload: { id: string } */
        SNAPSHOT_REMOVED: 'snapshot:removed',
        /** All snapshots cleared. No payload. */
        SNAPSHOTS_CLEARED: 'snapshot:cleared',
        /** Snapshot updated (annotations, description, report inclusion). Payload: ViewportSnapshot */
        SNAPSHOT_UPDATED: 'snapshot:updated',
        /** Snapshot restored from server hydration (not a new capture). Payload: ViewportSnapshot */
        SNAPSHOT_RESTORED: 'snapshot:restored',
        /**
         * A report-template capture pass recorded its result.
         * Payload: { templateId: string, ids: string[], signature: string | null }
         */
        TEMPLATE_CAPTURE_RECORDED: 'snapshot:template-capture-recorded',
        /** Draft captured and ready for preview. Payload: ViewportSnapshot */
        DRAFT_CAPTURED: 'snapshot:draft:captured',
        /** Draft dismissed (user closed preview without saving). No payload. */
        DRAFT_DISMISSED: 'snapshot:draft:dismissed',
    } as const;

    private readonly store = new Map<string, ViewportSnapshot>();
    private readonly capturedTemplates = new Map<string, TemplateCaptureResult>();
    private pendingDraft: ViewportSnapshot | null = null;

    constructor(private readonly context: ServiceContext) {
        super();
    }

    // ========================================================================
    // Draft workflow (capture → preview → save / dismiss)
    // ========================================================================

    /**
     * Capture a viewport and hold the result as a pending draft.
     * Captures both overlay and no-overlay variants atomically; both are kept
     * on the snapshot so the base can still be switched after saving.
     * Broadcasts DRAFT_CAPTURED with { snapshot }.
     */
    async captureDraft(viewportId: string): Promise<ViewportSnapshot | null> {
        const viewportService = this.context.services.getService<ViewportService>(
            ViewportService.SERVICE_NAME
        );

        const viewport = viewportService.getViewport(viewportId);
        if (!viewport) return null;

        const [dataUrl, dataUrlNoOverlay] = await Promise.all([
            viewportService.captureViewportSnapshot(viewportId, {
                mimeType: 'image/png',
                captureOverlay: true,
            }),
            viewportService.captureViewportSnapshot(viewportId, {
                mimeType: 'image/png',
                captureOverlay: false,
            }),
        ]);
        if (!dataUrl || !dataUrlNoOverlay) return null;

        const draft: ViewportSnapshot = {
            id: crypto.randomUUID(),
            viewMode: VIEWPORT_TYPE_TO_VIEW_MODE[viewport.type],
            viewportId,
            dataUrl,
            dataUrlNoOverlay,
            baseVariant: 'overlay',
            annotations: [],
            capturedAt: new Date().toISOString(),
            includedInReport: true,
            origin: 'user',
        };

        this.pendingDraft = draft;
        this.broadcast(this.EVENTS.DRAFT_CAPTURED, { snapshot: draft });
        return draft;
    }

    /**
     * Persist the pending draft (optionally with modifications like a chosen dataUrl).
     * Broadcasts SNAPSHOT_STORED + DRAFT_DISMISSED.
     */
    saveDraft(overrides?: Partial<ViewportSnapshot>): void {
        if (!this.pendingDraft) return;
        const snapshot = overrides ? { ...this.pendingDraft, ...overrides } : this.pendingDraft;
        this.store.set(snapshot.id, snapshot);
        this.pendingDraft = null;
        this.broadcast(this.EVENTS.SNAPSHOT_STORED, snapshot);
        this.broadcast(this.EVENTS.DRAFT_DISMISSED);
    }

    /**
     * Discard the pending draft without saving.
     * Broadcasts DRAFT_DISMISSED.
     */
    dismissDraft(): void {
        if (!this.pendingDraft) return;
        this.pendingDraft = null;
        this.broadcast(this.EVENTS.DRAFT_DISMISSED);
    }

    /** Get the current pending draft, or null. */
    getPendingDraft(): ViewportSnapshot | null {
        return this.pendingDraft;
    }

    // ========================================================================
    // Direct capture + store (used by report pipeline)
    // ========================================================================

    /**
     * Capture a viewport by ID and store immediately (used by report snapshots).
     */
    async captureViewportById(
        viewportId: string,
        label?: string,
        templateConfigId?: string,
        configId?: string
    ): Promise<ViewportSnapshot | null> {
        const snapshot = await this.captureImage(viewportId, label, templateConfigId, configId);
        if (!snapshot) return null;

        this.store.set(snapshot.id, snapshot);
        this.broadcast(this.EVENTS.SNAPSHOT_STORED, snapshot);
        return snapshot;
    }

    /**
     * Seed the store with a snapshot restored from server-side persistence
     * (hydration). Emits SNAPSHOT_RESTORED — never SNAPSHOT_STORED — so the
     * persistence sync hook does not re-POST an already-persisted snapshot,
     * while UI consumers still rebuild. Pass `{ silent: true }` to seed
     * without notifying any subscriber.
     */
    restoreSnapshot(snapshot: ViewportSnapshot, opts?: { silent?: boolean }): void {
        this.store.set(snapshot.id, snapshot);
        if (!opts?.silent) {
            this.broadcast(this.EVENTS.SNAPSHOT_RESTORED, snapshot);
        }
    }

    // ========================================================================
    // Queries
    // ========================================================================

    getSnapshot(id: string): ViewportSnapshot | undefined {
        return this.store.get(id);
    }

    getAllSnapshots(): ViewportSnapshot[] {
        return Array.from(this.store.values());
    }

    // ========================================================================
    // Mutations
    // ========================================================================

    toggleReportInclusion(id: string): void {
        const snapshot = this.store.get(id);
        if (!snapshot) return;

        const updated: ViewportSnapshot = {
            ...snapshot,
            includedInReport: !snapshot.includedInReport,
        };
        this.store.set(id, updated);
        this.broadcast(this.EVENTS.SNAPSHOT_UPDATED, updated);
    }

    /**
     * Replace a snapshot's annotations and refresh the cached composite.
     * The native captures are left untouched. Passing an empty array clears
     * the composite so display falls back to the native base.
     */
    updateSnapshotAnnotations(
        id: string,
        annotations: AnnotationData[],
        annotatedDataUrl: string | null,
        baseVariant: SnapshotBaseVariant
    ): void {
        const snapshot = this.store.get(id);
        if (!snapshot) return;

        const updated: ViewportSnapshot = {
            ...snapshot,
            annotations,
            annotatedDataUrl: annotations.length > 0 && annotatedDataUrl ? annotatedDataUrl : undefined,
            baseVariant,
        };
        this.store.set(id, updated);
        this.broadcast(this.EVENTS.SNAPSHOT_UPDATED, updated);
    }

    updateSnapshotDescription(id: string, description: string): void {
        const snapshot = this.store.get(id);
        if (!snapshot) return;

        const updated: ViewportSnapshot = { ...snapshot, description };
        this.store.set(id, updated);
        this.broadcast(this.EVENTS.SNAPSHOT_UPDATED, updated);
    }

    /**
     * Record the snapshot IDs produced by a template capture pass, along with the
     * data signature they were captured from. The signature lets the report
     * handler skip a redundant re-capture on remount when the data is unchanged.
     * Broadcasts TEMPLATE_CAPTURE_RECORDED so the persistence hook can re-apply
     * persisted annotations onto the freshly regenerated snapshots.
     */
    recordTemplateCaptureResult(templateId: string, snapshotIds: string[], signature: string | null): void {
        this.capturedTemplates.set(templateId, { ids: snapshotIds, signature });
        this.broadcast(this.EVENTS.TEMPLATE_CAPTURE_RECORDED, {
            templateId,
            ids: snapshotIds,
            signature,
        });
    }

    getTemplateCaptureResult(templateId: string): TemplateCaptureResult | undefined {
        return this.capturedTemplates.get(templateId);
    }

    removeSnapshot(id: string): void {
        if (this.store.delete(id)) {
            this.broadcast(this.EVENTS.SNAPSHOT_REMOVED, { id });
        }
    }

    clearAll(): void {
        this.store.clear();
        this.broadcast(this.EVENTS.SNAPSHOTS_CLEARED);
    }

    override reset(): void {
        this.pendingDraft = null;
        this.store.clear();
        this.capturedTemplates.clear();
        super.reset();
    }

    // ========================================================================
    // Internal helpers
    // ========================================================================

    private async captureImage(
        viewportId: string,
        label?: string,
        templateConfigId?: string,
        configId?: string
    ): Promise<ViewportSnapshot | null> {
        const viewportService = this.context.services.getService<ViewportService>(
            ViewportService.SERVICE_NAME
        );

        const viewport = viewportService.getViewport(viewportId);
        if (!viewport) return null;

        const dataUrl = await viewportService.captureViewportSnapshot(viewportId, {
            mimeType: 'image/png',
            captureOverlay: true,
        });
        if (!dataUrl) return null;

        // Template captures stay single-variant: they run as a batch over hidden
        // viewports and the overlay is the point of the capture.
        return {
            id: crypto.randomUUID(),
            viewMode: VIEWPORT_TYPE_TO_VIEW_MODE[viewport.type],
            viewportId,
            dataUrl,
            baseVariant: 'overlay',
            annotations: [],
            capturedAt: new Date().toISOString(),
            label,
            includedInReport: true,
            origin: 'template',
            templateConfigId,
            configId,
        };
    }
}
